import type { Token } from '@vif/core';
import { clusterCount, orpCluster } from '@vif/core';
import monoMetrics from '@vif/fonts/metrics.json';
import plexMetrics from '@vif/fonts/metrics-vif-neue.json';
import codeMetrics from '@vif/fonts/metrics-vif-code.json';
import dyslexicMetrics from '@vif/fonts/metrics-vif-dyslexic.json';
// Best-effort proportional set (P2 d9). Metrics only (data) — the layout math
// below is shared verbatim with the web reader; web ships the FONT FILES for
// only the four faces above until the deferred web-adoption follow-up.
import literataMetrics from '@vif/fonts/metrics-literata.json';
import sourceSerifMetrics from '@vif/fonts/metrics-source-serif.json';
import garamondMetrics from '@vif/fonts/metrics-eb-garamond.json';
import lyricMetrics from '@vif/fonts/metrics-vif-lyric.json';
import gazetteMetrics from '@vif/fonts/metrics-vif-gazette.json';
import slabMetrics from '@vif/fonts/metrics-vif-slab.json';
import newsreaderMetrics from '@vif/fonts/metrics-newsreader.json';
import spectralMetrics from '@vif/fonts/metrics-spectral.json';
import vollkornMetrics from '@vif/fonts/metrics-vollkorn.json';
import atkinsonMetrics from '@vif/fonts/metrics-atkinson.json';
import interMetrics from '@vif/fonts/metrics-inter.json';
import publicSansMetrics from '@vif/fonts/metrics-public-sans.json';

/**
 * Monospace-perfect ORP layout (PRD §8.1/§8.2). The ORP glyph's horizontal
 * CENTER sits at a fixed anchor x for the whole session; words overflow
 * left/right without moving the anchor. Position is computed from bundled
 * font metrics — summed glyph advances, never DOM measurement — so it is
 * exact for the mono fonts and deterministic (±browser hinting) for the
 * proportional one, with kerning/ligatures disabled at the use site.
 *
 * H1 (hands-on fix-pass): the anchor is 38% of the READING STAGE, not the
 * viewport. On wide screens the stage is a centered column capped at
 * 44rem (704 px @16px root — a comfortable long-form reading measure);
 * below the cap the stage is the full width, so phone behavior is
 * unchanged and the pre-H1 anchor math still holds there.
 */

export const ORP_ANCHOR_FRACTION = 0.38;
/** Reading stage cap: 44rem. Exported in px because all layout math is px. */
export const READING_STAGE_MAX_PX = 44 * 16;

/** Width of the centered reading stage for a given container width. */
export function readingStageWidth(containerWidth: number): number {
  return Math.min(containerWidth, READING_STAGE_MAX_PX);
}

/** The session-fixed anchor x, relative to the reading stage's left edge. */
export function anchorXForStage(stageWidth: number): number {
  return stageWidth * ORP_ANCHOR_FRACTION;
}

/**
 * The four faces the web reader ships and keys its font records on. Monospace
 * mono/plex/code are the pixel-guaranteed gated experience (§7); dyslexic is
 * best-effort proportional.
 */
export type ReaderFont = 'mono' | 'plex' | 'code' | 'dyslexic';

/**
 * Every reader font the layout engine can measure: `ReaderFont` plus the P2 d9
 * best-effort proportional set. Mobile uses this end-to-end; web stays on
 * `ReaderFont` (its font records + picker) until the deferred web-adoption
 * follow-up. The monospace path below is unchanged — the new faces are all
 * proportional and travel the summed-advance path.
 */
export type ReaderFontId =
  | ReaderFont
  | 'literata'
  | 'sourceSerif'
  | 'garamond'
  | 'lyric'
  | 'gazette'
  | 'slab'
  | 'newsreader'
  | 'spectral'
  | 'vollkorn'
  | 'atkinson'
  | 'inter'
  | 'publicSans';

/** advanceWidthEm per monospace face (each passes the fonts build gate). */
const MONO_ADVANCE_EM: Partial<Record<ReaderFontId, number>> = {
  mono: monoMetrics.advanceWidthEm,
  plex: plexMetrics.advanceWidthEm,
  code: codeMetrics.advanceWidthEm,
};

interface ProportionalMetrics {
  readonly unitsPerEm: number;
  readonly advances: Readonly<Record<string, number>>;
}

interface ProportionalFace {
  readonly unitsPerEm: number;
  readonly advances: Readonly<Record<string, number>>;
  /** Fallback for codepoints outside the subset: average advance. */
  readonly defaultAdvance: number;
}

function proportionalFace(metrics: ProportionalMetrics): ProportionalFace {
  const values = Object.values(metrics.advances);
  const defaultAdvance = values.reduce((a, b) => a + b, 0) / values.length;
  return { unitsPerEm: metrics.unitsPerEm, advances: metrics.advances, defaultAdvance };
}

/** Proportional advance tables keyed by font id — the summed-advance ORP path
 * (PRD §8.2), shared by Vif Dyslexic and the d9 best-effort set. */
const PROPORTIONAL: Partial<Record<ReaderFontId, ProportionalFace>> = {
  dyslexic: proportionalFace(dyslexicMetrics as ProportionalMetrics),
  literata: proportionalFace(literataMetrics as ProportionalMetrics),
  sourceSerif: proportionalFace(sourceSerifMetrics as ProportionalMetrics),
  garamond: proportionalFace(garamondMetrics as ProportionalMetrics),
  lyric: proportionalFace(lyricMetrics as ProportionalMetrics),
  gazette: proportionalFace(gazetteMetrics as ProportionalMetrics),
  slab: proportionalFace(slabMetrics as ProportionalMetrics),
  newsreader: proportionalFace(newsreaderMetrics as ProportionalMetrics),
  spectral: proportionalFace(spectralMetrics as ProportionalMetrics),
  vollkorn: proportionalFace(vollkornMetrics as ProportionalMetrics),
  atkinson: proportionalFace(atkinsonMetrics as ProportionalMetrics),
  inter: proportionalFace(interMetrics as ProportionalMetrics),
  publicSans: proportionalFace(publicSansMetrics as ProportionalMetrics),
};

/** Advance of one grapheme cluster, in px at the given font size. Monospace
 * faces use their single advanceWidthEm (the gated path, unchanged); every
 * other face sums its per-glyph proportional advances. */
function clusterAdvancePx(font: ReaderFontId, cluster: string, fontSizePx: number): number {
  const em = MONO_ADVANCE_EM[font];
  if (em !== undefined) return em * fontSizePx;
  const face = PROPORTIONAL[font] ?? PROPORTIONAL.dyslexic!;
  let units = 0;
  for (const ch of cluster) {
    units += face.advances[String(ch.codePointAt(0))] ?? face.defaultAdvance;
  }
  return (units / face.unitsPerEm) * fontSizePx;
}

export interface OrpLayout {
  /** CSS left of the word box so the ORP glyph center lands on anchorX. */
  readonly left: number;
  /** Raw string split for three-span rendering. */
  readonly before: string;
  readonly orp: string;
  readonly after: string;
  /** Total advance width of the word (px). */
  readonly width: number;
  /** Offset of the ORP glyph center inside the word box (px). */
  readonly orpCenter: number;
}

/** Slice a token's raw text at grapheme-cluster boundaries. */
function clusterSlices(token: Token, orpIdx: number): [string, string, string] {
  const offsets = token.clusterToUnitOffsets;
  const start = offsets[orpIdx] ?? 0;
  const end = offsets[orpIdx + 1] ?? token.raw.length;
  return [token.raw.slice(0, start), token.raw.slice(start, end), token.raw.slice(end)];
}

/** Advance of one space — chunk members are visually space-separated. */
export function spaceAdvancePx(font: ReaderFontId, fontSizePx: number): number {
  return clusterAdvancePx(font, ' ', fontSizePx);
}

export interface ChunkMemberLayout {
  readonly token: Token;
  /** Word split + metric widths for three-span rendering. */
  readonly word: OrpLayout;
  /** Absolute CSS left of the member's word box. */
  readonly left: number;
  /** Whether this member carries the ORP highlight. */
  readonly highlighted: boolean;
}

/**
 * Chunk layout per PRD §7.5 (P1-F4). Vertical: the LONGEST member (by
 * metric width) defines the anchor — its ORP center sits on anchorX — and
 * every row shares its left edge; only that word carries the ORP highlight.
 * Horizontal: members flow in one row with the FIRST word's ORP on the
 * anchor and only the first word highlighted. A single-member chunk is the
 * plain RSVP case in both layouts: its own ORP on the anchor, highlighted.
 */
export function layoutChunk(
  tokens: readonly Token[],
  layout: 'vertical' | 'horizontal',
  font: ReaderFontId,
  fontSizePx: number,
  anchorX: number,
): ChunkMemberLayout[][] {
  const words = tokens.map((token) => layoutWord(token, font, fontSizePx, 0));

  if (layout === 'vertical') {
    let longestIdx = 0;
    for (const [i, word] of words.entries()) {
      if (word.width > words[longestIdx]!.width) longestIdx = i;
    }
    const left = anchorX - words[longestIdx]!.orpCenter;
    return tokens.map((token, i) => [
      { token, word: words[i]!, left, highlighted: i === longestIdx },
    ]);
  }

  const space = spaceAdvancePx(font, fontSizePx);
  let x = anchorX - (words[0]?.orpCenter ?? 0);
  return [
    tokens.map((token, i) => {
      const member = { token, word: words[i]!, left: x, highlighted: i === 0 };
      x += words[i]!.width + space;
      return member;
    }),
  ];
}

export function layoutWord(
  token: Token,
  font: ReaderFontId,
  fontSizePx: number,
  anchorX: number,
): OrpLayout {
  const orpIdx = orpCluster(token);
  const [before, orp, after] = clusterSlices(token, orpIdx);

  const clusters = clusterCount(token);
  let widthBeforeOrp = 0;
  let orpAdvance = 0;
  let width = 0;
  for (let i = 0; i < clusters; i++) {
    const offsets = token.clusterToUnitOffsets;
    const cluster = token.raw.slice(offsets[i], offsets[i + 1]);
    const advance = clusterAdvancePx(font, cluster, fontSizePx);
    if (i < orpIdx) widthBeforeOrp += advance;
    if (i === orpIdx) orpAdvance = advance;
    width += advance;
  }
  const orpCenter = widthBeforeOrp + orpAdvance / 2;
  return { left: anchorX - orpCenter, before, orp, after, width, orpCenter };
}
